import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  Home,
  UserCheck,
  FileText,
  Calendar,
  CreditCard,
  Receipt,
  Settings,
  LogOut,
  Building,
  FileBarChart,
  Landmark,
  ArrowLeftRight,
  Link2,
  ScrollText,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { Button } from '@/components/ui/button';

const menuItems = [
  { title: 'Tableau de bord', icon: LayoutDashboard, path: '/dashboard', adminOnly: false },
  { title: 'Synthèse Patrimoine', icon: FileBarChart, path: '/analytics', adminOnly: false },
  { title: 'Utilisateurs', icon: Building, path: '/clients', adminOnly: false },
  { title: 'SCI', icon: Building2, path: '/scis', adminOnly: false },
  { title: 'Biens', icon: Home, path: '/properties', adminOnly: false },
  { title: 'Locataires', icon: UserCheck, path: '/tenants', adminOnly: false },
  { title: 'Contrats', icon: FileText, path: '/contracts', adminOnly: false },
  { title: 'Quittances', icon: Receipt, path: '/receipts', adminOnly: false },
];

const bankMenuItems = [
  { title: 'Connexions bancaires', icon: Link2, path: '/bank-connections' },
  { title: 'Transactions', icon: Landmark, path: '/transactions' },
  { title: 'Rapprochement', icon: ArrowLeftRight, path: '/reconciliation' },
];

const settingsItems = [
  { title: 'Équipe', icon: Users, path: '/team', adminOnly: false },
  { title: 'SUPER PDP', icon: CreditCard, path: '/superpdp', adminOnly: false },
  { title: "Journaux d'audit", icon: ScrollText, path: '/logs', adminOnly: true },
  { title: 'Paramètres', icon: Settings, path: '/settings', adminOnly: false },
];

export function AppSidebar() {
  const location = useLocation();
  const { signOut } = useAuth();
  const { isAdmin } = useUserRole();

  const visibleMenuItems = menuItems.filter(item => !item.adminOnly || isAdmin);
  const visibleSettingsItems = settingsItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border px-6 py-4">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg gradient-primary">
            <Building className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-display text-lg font-semibold text-sidebar-foreground">
              izimmo
            </h1>
            <p className="text-xs text-sidebar-foreground/60">Gestion locative</p>
          </div>
        </Link>
      </SidebarHeader>
      
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/50 uppercase text-xs tracking-wider">
            Menu principal
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleMenuItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === item.path}
                    className="transition-all duration-200"
                  >
                    <Link to={item.path}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/50 uppercase text-xs tracking-wider">
            Banque
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {bankMenuItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === item.path}
                  >
                    <Link to={item.path}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/50 uppercase text-xs tracking-wider">
            Configuration
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleSettingsItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === item.path}
                  >
                    <Link to={item.path}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        <Button
          variant="ghost"
          className="w-full justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
          onClick={signOut}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Déconnexion
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}